import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Smartphone, Package } from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useLanguage } from '../contexts/LanguageContext';

interface Brand {
  id: number;
  name: string;
  description?: string;
  model_count: number;
  phone_count: number;
}

interface Model {
  id: number;
  brand_id: number;
  name: string;
  description?: string;
  brand_name: string;
  phone_count: number;
  image_url?: string;
}

const BrandModelManagement: React.FC = () => {
  const { t } = useLanguage();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'brands' | 'models'>('brands');
  
  // Brand form states
  const [showBrandForm, setShowBrandForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
  const [brandForm, setBrandForm] = useState({ name: '', description: '' });
  
  // Model form states
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [modelForm, setModelForm] = useState({ brand_id: '', name: '', description: '', image_url: '' });
  const [modelImageFile, setModelImageFile] = useState<File | null>(null);
  const [modelImagePreview, setModelImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [brandsResponse, modelsResponse] = await Promise.all([
        fetch('/api/brands', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }),
        fetch('/api/models', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        })
      ]);

      if (brandsResponse.ok && modelsResponse.ok) {
        const brandsData = await brandsResponse.json();
        const modelsData = await modelsResponse.json();
        setBrands(brandsData);
        setModels(modelsData);
      } else {
        showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errLoadData'));
      }
    } catch (error) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleBrandSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandForm.name.trim()) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errBrandRequired'));
      return;
    }

    try {
      const url = editingBrand ? `/api/brands/${editingBrand.id}` : '/api/brands';
      const method = editingBrand ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(brandForm)
      });

      if (response.ok) {
        showGlobalNotification.success(t('brandmodel.successTitle'), editingBrand ? t('brandmodel.successBrandUpdated') : t('brandmodel.successBrandAdded'));
        setShowBrandForm(false);
        setEditingBrand(null);
        setBrandForm({ name: '', description: '' });
        loadData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('brandmodel.errTitle'), error.error || t('brandmodel.errOpFailed'));
      }
    } catch (error) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errOpFailed'));
    }
  };

  const handleModelSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modelForm.brand_id || !modelForm.name.trim()) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errBrandModelRequired'));
      return;
    }

    try {
      let finalImageUrl = modelForm.image_url;

      if (modelImageFile) {
        setUploadingImage(true);
        const imageFormData = new FormData();
        imageFormData.append('photo', modelImageFile);

        const uploadResponse = await fetch('/api/upload/model-photo', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: imageFormData
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          finalImageUrl = uploadData.path;
        } else {
          showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errImageUpload'));
          setUploadingImage(false);
          return;
        }
      }

      const url = editingModel ? `/api/models/${editingModel.id}` : '/api/models';
      const method = editingModel ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          ...modelForm,
          image_url: finalImageUrl,
          brand_id: parseInt(modelForm.brand_id)
        })
      });

      if (response.ok) {
        showGlobalNotification.success(t('brandmodel.successTitle'), editingModel ? t('brandmodel.successModelUpdated') : t('brandmodel.successModelAdded'));
        setShowModelForm(false);
        setEditingModel(null);
        setModelForm({ brand_id: '', name: '', description: '', image_url: '' });
        setModelImageFile(null);
        setModelImagePreview(null);
        setUploadingImage(false);
        loadData();
      } else {
        const error = await response.json();
        setUploadingImage(false);
        showGlobalNotification.error(t('brandmodel.errTitle'), error.error || t('brandmodel.errOpFailed'));
      }
    } catch (error) {
      setUploadingImage(false);
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errOpFailed'));
    }
  };

  const handleDeleteBrand = async (brand: Brand) => {
    if (brand.phone_count > 0) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errBrandHasPhones', { count: brand.phone_count }));
      return;
    }

    if (!confirm(t('brandmodel.confirmDeleteBrand', { name: brand.name }))) {
      return;
    }

    try {
      const response = await fetch(`/api/brands/${brand.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        showGlobalNotification.success(t('brandmodel.successTitle'), t('brandmodel.successBrandDeleted'));
        loadData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('brandmodel.errTitle'), error.error || t('brandmodel.errBrandDeleted'));
      }
    } catch (error) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errBrandDeleted'));
    }
  };

  const handleDeleteModel = async (model: Model) => {
    if (model.phone_count > 0) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errModelHasPhones', { count: model.phone_count }));
      return;
    }

    if (!confirm(t('brandmodel.confirmDeleteModel', { brand: model.brand_name, name: model.name }))) {
      return;
    }

    try {
      const response = await fetch(`/api/models/${model.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });

      if (response.ok) {
        showGlobalNotification.success(t('brandmodel.successTitle'), t('brandmodel.successModelDeleted'));
        loadData();
      } else {
        const error = await response.json();
        showGlobalNotification.error(t('brandmodel.errTitle'), error.error || t('brandmodel.errModelDeleted'));
      }
    } catch (error) {
      showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errModelDeleted'));
    }
  };

  const openBrandEdit = (brand: Brand) => {
    setEditingBrand(brand);
    setBrandForm({ name: brand.name, description: brand.description || '' });
    setShowBrandForm(true);
  };

  const openModelEdit = (model: Model) => {
    setEditingModel(model);
    setModelForm({ 
      brand_id: model.brand_id.toString(), 
      name: model.name, 
      description: model.description || '',
      image_url: model.image_url || ''
    });
    setModelImagePreview(model.image_url || null);
    setModelImageFile(null);
    setShowModelForm(true);
  };

  const cancelForm = () => {
    setShowBrandForm(false);
    setShowModelForm(false);
    setEditingBrand(null);
    setEditingModel(null);
    setBrandForm({ name: '', description: '' });
    setModelForm({ brand_id: '', name: '', description: '', image_url: '' });
    setModelImageFile(null);
    setModelImagePreview(null);
  };

  const handleModelImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showGlobalNotification.error(t('brandmodel.errTitle'), t('brandmodel.errFileTooLarge'));
        return;
      }
      setModelImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setModelImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('brandmodel.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('brandmodel.subtitle')}</p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('brands')}
              className={`py-3 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'brands'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <Smartphone className="w-4 h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('brandmodel.tabBrands')}</span>
              <span className="sm:hidden">{t('brandmodel.tabBrandsShort')}</span>
              <span className="ml-1">({brands.length})</span>
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`py-3 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'models'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600'
              }`}
            >
              <Package className="w-4 h-4 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">{t('brandmodel.tabModels')}</span>
              <span className="sm:hidden">{t('brandmodel.tabModelsShort')}</span>
              <span className="ml-1">({models.length})</span>
            </button>
          </nav>
        </div>
      </div>

      {/* Brands Tab */}
      {activeTab === 'brands' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('brandmodel.brandsHeading')}</h2>
            <button
              onClick={() => setShowBrandForm(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('brandmodel.newBrand')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colBrandName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colDescription')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colModelCount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colPhoneCount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {brands.map((brand) => (
                  <tr key={brand.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {brand.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {brand.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {brand.model_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {brand.phone_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openBrandEdit(brand)}
                          className="text-primary hover:text-primary-dark"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand)}
                          className="text-red-600 hover:text-red-900"
                          disabled={brand.phone_count > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Models Tab */}
      {activeTab === 'models' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('brandmodel.modelsHeading')}</h2>
            <button
              onClick={() => setShowModelForm(true)}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('brandmodel.newModel')}
            </button>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colBrand')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colImage')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colModelName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colDescription')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colPhoneCount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('brandmodel.colActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {models.map((model) => (
                  <tr key={model.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {model.brand_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {model.image_url ? (
                        <img src={model.image_url} alt={model.name} className="h-10 w-10 object-contain rounded bg-white" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-gray-100 flex items-center justify-center">
                          <Smartphone className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                      {model.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {model.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {model.phone_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openModelEdit(model)}
                          className="text-primary hover:text-primary-dark"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model)}
                          className="text-red-600 hover:text-red-900"
                          disabled={model.phone_count > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Brand Form Modal */}
      {showBrandForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                {editingBrand ? t('brandmodel.editBrandTitle') : t('brandmodel.addBrandTitle')}
              </h3>
              <form onSubmit={handleBrandSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelBrandName')}
                  </label>
                  <input
                    type="text"
                    value={brandForm.name}
                    onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelDescription')}
                  </label>
                  <textarea
                    value={brandForm.description}
                    onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={cancelForm}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    <X className="w-4 h-4 inline mr-2" />
                    {t('brandmodel.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                  >
                    <Save className="w-4 h-4 inline mr-2" />
                    {editingBrand ? t('brandmodel.update') : t('brandmodel.add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Model Form Modal */}
      {showModelForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                {editingModel ? t('brandmodel.editModelTitle') : t('brandmodel.addModelTitle')}
              </h3>
              <form onSubmit={handleModelSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelBrand')}
                  </label>
                  <select
                    value={modelForm.brand_id}
                    onChange={(e) => setModelForm({ ...modelForm, brand_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  >
                    <option value="">{t('brandmodel.placeholderBrand')}</option>
                    {brands.map((brand) => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelModelName')}
                  </label>
                  <input
                    type="text"
                    value={modelForm.name}
                    onChange={(e) => setModelForm({ ...modelForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelDescription')}
                  </label>
                  <textarea
                    value={modelForm.description}
                    onChange={(e) => setModelForm({ ...modelForm, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('brandmodel.labelModelImage')}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleModelImageChange}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary-dark"
                  />
                  {modelImagePreview && (
                    <div className="mt-2">
                      <img src={modelImagePreview} alt={t('brandmodel.previewAlt')} className="h-32 object-contain bg-white rounded border border-gray-200" />
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={cancelForm}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700"
                  >
                    <X className="w-4 h-4 inline mr-2" />
                    {t('brandmodel.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImage}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50 flex items-center"
                  >
                    {uploadingImage ? (
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Save className="w-4 h-4 inline mr-2" />
                    )}
                    {editingModel ? t('brandmodel.update') : t('brandmodel.add')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandModelManagement;
